import { useEffect, useRef, useState } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const parseYmd = (value?: string | null) => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const formatYmd = (date: Date) => format(date, 'yyyy-MM-dd');

interface WeekRangeDatePickerProps {
  value: string;
  onChange: (value: string) => void;
  rangeStart?: string;
  rangeEnd?: string;
  min?: string;
  allowedWeekday?: number;
  ariaInvalid?: boolean;
  disabled?: boolean;
  className: string;
  placeholder?: string;
}

export function WeekRangeDatePicker({
  value,
  onChange,
  rangeStart,
  rangeEnd,
  min,
  allowedWeekday,
  ariaInvalid,
  disabled,
  className,
  placeholder = 'Select date',
}: WeekRangeDatePickerProps) {
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const [openAbove, setOpenAbove] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => parseYmd(value) ?? new Date());

  const containerRef = useRef<HTMLDivElement>(null);

  const selectedDate = parseYmd(value);
  const rangeStartDate = parseYmd(rangeStart);
  const rangeEndDate = parseYmd(rangeEnd);
  const minDate = parseYmd(min);

  useEffect(() => {
    if (open) {
      setViewMonth(selectedDate ?? rangeStartDate ?? new Date());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const updatePosition = () => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();

      const calendarHeight = 340;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      setOpenAbove(
        spaceBelow < calendarHeight && spaceAbove > calendarHeight
      );
    };

    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () =>
      document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const gridStart = startOfWeek(startOfMonth(viewMonth), {
    weekStartsOn: 1,
  });

  const gridEnd = endOfWeek(endOfMonth(viewMonth), {
    weekStartsOn: 1,
  });

  const days = eachDayOfInterval({
    start: gridStart,
    end: gridEnd,
  });

  const handleSelect = (day: Date) => {
    if (minDate && isBefore(day, minDate)) return;
    if (allowedWeekday !== undefined && day.getDay() !== allowedWeekday) return;

    onChange(formatYmd(day));
    setOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        aria-invalid={ariaInvalid}
        onClick={() => setOpen((current) => !current)}
        className={`${className} flex items-center justify-between gap-2 text-left`}
        style={{
          color: theme.text,
          // Selected range fields use a light-blue utility class. Replace it
          // with a dark surface when the active theme is dark so the date
          // remains readable.
          backgroundColor: theme.isDark && className.includes('bg-blue-50')
            ? theme.surfaceHover
            : undefined,
        }}
      >
        <span className={selectedDate ? '' : 'text-gray-400'}>
          {selectedDate
            ? format(selectedDate, 'MMM d, yyyy')
            : placeholder}
        </span>

        <CalendarIcon className="h-4 w-4 shrink-0 text-gray-400" />
      </button>

      {open && !disabled && (
        <div
          className={`absolute left-0 z-50 w-72 rounded-lg border border-gray-200 bg-white p-3 shadow-lg ${
            openAbove ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() =>
                setViewMonth((current) => subMonths(current, 1))
              }
              className="rounded p-1 text-gray-500 hover:bg-gray-100"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <span className="text-sm font-semibold text-gray-700">
              {format(viewMonth, 'MMMM yyyy')}
            </span>

            <button
              type="button"
              onClick={() =>
                setViewMonth((current) => addMonths(current, 1))
              }
              className="rounded p-1 text-gray-500 hover:bg-gray-100"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-y-1 text-center text-xs font-semibold text-gray-400">
            {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>

          <div className="mt-1 grid grid-cols-7 gap-y-1">
            {days.map((day) => {
              const inCurrentMonth = isSameMonth(day, viewMonth);

              const isRangeBound = Boolean(
                (rangeStartDate &&
                  isSameDay(day, rangeStartDate)) ||
                  (rangeEndDate &&
                    isSameDay(day, rangeEndDate))
              );

              const inRange = Boolean(
                rangeStartDate &&
                  rangeEndDate &&
                  isWithinInterval(day, {
                    start: rangeStartDate,
                    end: rangeEndDate,
                  })
              );

              const isSelected = Boolean(
                selectedDate && isSameDay(day, selectedDate)
              );

              const isDisabled = Boolean(
                (minDate && isBefore(day, minDate)) ||
                (allowedWeekday !== undefined && day.getDay() !== allowedWeekday)
              );

              const dayClasses = [
                'flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium transition',
              ];

              if (isDisabled) {
                dayClasses.push(
                  'cursor-not-allowed text-gray-200'
                );
              } else if (isRangeBound) {
                dayClasses.push(
                  'bg-blue-500 text-white hover:bg-blue-600'
                );
              } else if (inRange) {
                dayClasses.push(
                  'bg-blue-100 text-blue-800 hover:bg-blue-200'
                );
              } else if (inCurrentMonth) {
                dayClasses.push(
                  'text-gray-700 hover:bg-gray-100'
                );
              } else {
                dayClasses.push(
                  'text-gray-300 hover:bg-gray-50'
                );
              }

              if (isSelected && !isRangeBound) {
                dayClasses.push('ring-2 ring-brand-primary');
              }

              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => handleSelect(day)}
                  className={dayClasses.join(' ')}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
